import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { MapContainer, GeoJSON, TileLayer } from "react-leaflet";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "leaflet/dist/leaflet.css";

// Styled components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const RevenueByPolandRegionChart = ({ salesData = [], clientsData = [] }) => {
  const [geoData, setGeoData] = useState(null);
  const [regionRevenueData, setRegionRevenueData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [error, setError] = useState(null);

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie",
    ŚLĄSKIE: "śląskie",
    PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie",
    ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie",
    DOLNOŚLĄSKIE: "dolnośląskie",
    ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie",
    POMORSKIE: "pomorskie",
    LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie",
    PODLASKIE: "podlaskie",
    "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie",
    MAŁOPOLSKIE: "małopolskie",
  };

  useEffect(() => {
    const fetchGeoData = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/ppatrzyk/polska-geojson/master/wojewodztwa/wojewodztwa-medium.geojson"
        );
        const data = await response.json();
        setGeoData(data);
      } catch {
        setError("Błąd ładowania danych mapy Polski.");
      }
    };
    fetchGeoData();
  }, []);

  useEffect(() => {
    if (salesData.length === 0) return;
    const years = [...new Set(salesData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear((prev) => prev || years[years.length - 1]);
  }, [salesData]);

  useEffect(() => {
    if (!selectedYear || salesData.length === 0 || clientsData.length === 0) return;

    const kontrahentaToRegion = {};
    salesData.forEach((row) => {
      const client = clientsData.find(
        (c) => c.nip?.trim() === row.NrKontrahenta?.trim()
      );
      if (client) {
        const normalizedRegion = client.wojewodztwo?.trim().toUpperCase();
        kontrahentaToRegion[row.NrKontrahenta?.trim()] =
          regionNameMapping[normalizedRegion] || "Unknown";
      } else {
        kontrahentaToRegion[row.NrKontrahenta?.trim()] = "Unknown";
      }
    });

    const filtered = salesData.filter(
      (row) =>
        row.Rok.toString() === selectedYear &&
        (selectedMonth === "all" || parseInt(row.Miesiac, 10) === parseInt(selectedMonth))
    );

    const groupedData = filtered.reduce((acc, row) => {
      const revenue =
        parseFloat(row.K_15) ||
        parseFloat(row.K_19) ||
        parseFloat(row.K_10) ||
        parseFloat(row.K_21) ||
        parseFloat(row.K_17) ||
        parseFloat(row.K_11) ||
        parseFloat(row.K_22) ||
        0;

      const region = kontrahentaToRegion[row.NrKontrahenta?.trim()] || "Unknown";
      acc[region] = (acc[region] || 0) + revenue;
      return acc;
    }, {});

    const validRegions = Object.keys(groupedData).filter((region) => region !== "Unknown");
    const validTotal = validRegions.reduce((sum, region) => sum + groupedData[region], 0);

    const formattedData = validRegions.map((region) => ({
      region,
      revenue: groupedData[region],
      percentage: validTotal ? ((groupedData[region] / validTotal) * 100).toFixed(2) : "0",
    }));

    setRegionRevenueData(formattedData);
    setTotalRevenue(validTotal);
  }, [salesData, clientsData, selectedYear, selectedMonth]);

  // ✅ Color scale (green good, red bad, 0 gray)
  // ≥50% dark green, 25–49% green, 10–24% yellow, 1–9% red, 0% gray
  const styleFeature = (feature) => {
    const regionName = feature.properties.nazwa.trim().toLowerCase();
    const revenueData = regionRevenueData.find((data) => data.region === regionName);
    const revenuePercentage = revenueData ? parseFloat(revenueData.percentage) : 0;

    const getColor = (p) => {
      if (p >= 50) return "#2e7d32";   // dark green
      if (p >= 25) return "#66bb6a";   // green
      if (p >= 10) return "#ffd54f";   // yellow
      if (p > 0)  return "#d32f2f";    // red (1–9%)
      return "#cccccc";                // gray (0%)
    };

    return {
      fillColor: getColor(revenuePercentage),
      weight: 0.8,
      opacity: 0.4,
      color: "#444",
      fillOpacity: 0.3,
    };
  };

  const onEachFeature = (feature, layer) => {
    const regionName = feature.properties.nazwa.trim().toLowerCase();
    const revenueData = regionRevenueData.find((data) => data.region === regionName);
    const revenue = revenueData
      ? `${revenueData.revenue.toLocaleString("pl-PL")}`
      : "Brak danych";
    const percentage = revenueData ? `${revenueData.percentage}%` : "0%";

    layer.bindPopup(
      `<strong>Region:</strong> ${regionName.toUpperCase()}<br/>
       <strong>Przychód:</strong> ${revenue}<br/>
       <strong>Udział:</strong> ${percentage}`
    );
  };

  return (
    <Wrapper>
      <ChartTitle>
        Udział Przychodów wg Województw
        <InfoIconWrapper data-tooltip-id="revenue-map-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="revenue-map-tooltip"
        place="left"
        content={
          <>
            <strong>Mapa udziału przychodów</strong><br />
            Kolor przedstawia udział przychodów województwa w całości.<br />
            Kliknij na region aby zobaczyć szczegóły.
          </>
        }
      />

      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="all">Cały rok</option>
            {monthNames.map((name, idx) => (
              <option key={idx} value={idx + 1}>{name}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      {!geoData || regionRevenueData.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888", textAlign: "center" }}>
          Brak danych do wyświetlenia.
        </p>
      ) : (
        <>
          <MapContainer
            style={{ height: "500px", width: "100%" }}
            bounds={[[49, 14], [55, 24]]}
            maxBounds={[[49, 14], [55, 24]]}
            zoom={6}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <GeoJSON
              data={geoData}
              style={styleFeature}
              onEachFeature={onEachFeature}
            />
          </MapContainer>

          {/* Legend */}
          <div
            style={{
              marginTop: "24px",
              padding: "20px",
              maxWidth: "800px",
              marginLeft: "auto",
              marginRight: "auto",
              textAlign: "left",
            }}
          >
            <h4 style={{
              marginBottom: "18px",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 600,
              color: "#333"
            }}>
              Legenda mapy udziałów przychodów
            </h4>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              {[
                { color: "#2e7d32", label: "≥ 50%",     desc: "bardzo wysoki udział (dobry)" },
                { color: "#66bb6a", label: "25% - 49%", desc: "wysoki udział (dobry)" },
                { color: "#ffd54f", label: "10% - 24%", desc: "średni udział (neutralny)" },
                { color: "#d32f2f", label: "1% - 9%",   desc: "niski udział (zły)" },
                { color: "#cccccc", label: "0%",        desc: "brak udziału" },
              ].map((item, index) => (
                <div
                  key={index}
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "6px",
                      backgroundColor: item.color,
                      border: "1px solid #999",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                    }}
                  />
                  <div style={{ lineHeight: "1.2" }}>
                    <div style={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: "12px", color: "#555" }}>
                      {item.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Wrapper>
  );
};

export default RevenueByPolandRegionChart;
